import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Plus, Edit, Trash2, Search, Building } from "lucide-react";

const TablasCentrosTrabajo = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", direccion: "", telefono: "" });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/work-centers`, { withCredentials: true });
      setItems(res.data);
    } catch (err) { console.error("Error loading work centers:", err); toast.error("Error al cargar"); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/work-centers/${editing.id}`, form, { withCredentials: true });
        toast.success("Centro de trabajo actualizado");
      } else {
        await axios.post(`${API}/work-centers`, form, { withCredentials: true });
        toast.success("Centro de trabajo creado");
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ nombre: "", direccion: "", telefono: "" });
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({ nombre: item.nombre || "", direccion: item.direccion || "", telefono: item.telefono || "" });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este centro de trabajo?")) return;
    try {
      await axios.delete(`${API}/work-centers/${id}`, { withCredentials: true });
      toast.success("Centro eliminado");
      fetchItems();
    } catch (err) { console.error("Error deleting work center:", err); toast.error("Error al eliminar"); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ nombre: "", direccion: "", telefono: "" });
    setShowDialog(true);
  };

  const filtered = items.filter(i =>
    i.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.direccion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="tablas-centros-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Building className="w-6 h-6 text-blue-600" /> Centros de Trabajo
              </h1>
              <p className="text-slate-500">Persona Moral — Ubicaciones de resguardo para activos fijos</p>
            </div>
          </div>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-center-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Centro
          </Button>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o dirección..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-centers"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>NOMBRE</TableHead>
                  <TableHead>DIRECCIÓN</TableHead>
                  <TableHead>TELÉFONO</TableHead>
                  <TableHead className="text-right">ACCIONES</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-10">Cargando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-500">No hay centros de trabajo</TableCell></TableRow>
                ) : (
                  filtered.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.nombre}</TableCell>
                      <TableCell>{item.direccion}</TableCell>
                      <TableCell>{item.telefono}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => handleEdit(item)} data-testid={`edit-center-${item.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-red-600" onClick={() => handleDelete(item.id)} data-testid={`delete-center-${item.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) setEditing(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar Centro de Trabajo" : "Nuevo Centro de Trabajo"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="center-nombre" />
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} data-testid="center-direccion" />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="center-telefono" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" data-testid="save-center-btn">{editing ? "Guardar Cambios" : "Crear"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TablasCentrosTrabajo;
